import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Ship, ChevronLeft, MapPin, Calendar, Anchor, Shield, AlertTriangle,
  CheckCircle2, Clock, Eye, FileText, Camera, ClipboardCheck, BarChart3,
  Settings, Wrench, Download, Printer, ArrowUpRight, Globe2, Weight,
  Ruler, Gauge, Fuel, CalendarDays, Building2, User, Info, TrendingUp,
  TrendingDown, ExternalLink, Tag, Layers, Award
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { mockInspections, mockCorrectiveActions, mockObservations, mockFleets } from '../../data/mockData';
import { formatDate, getScoreColor, getScoreBgColor, getInspectionStatusLabel, getInspectionStatusColor, getCAPAStatusLabel, getCAPAStatusColor, getSeverityColor } from '../../utils/helpers';
import type { Vessel, VesselCertificate } from '../../types';

type TabId = 'overview' | 'certificates' | 'inspections' | 'capa' | 'analytics' | 'specifications';

export default function VesselDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { vessels } = useAppStore();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const vessel = vessels.find(v => v.id === id) || vessels[0];
  if (!vessel) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Ship className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Vessel not found</p>
          <button onClick={() => navigate('/app/fleet')} className="mt-3 px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600">
            Back to Fleet
          </button>
        </div>
      </div>
    );
  }

  const fleet = mockFleets.find(f => f.id === vessel.fleetId);
  const vesselInspections = mockInspections.filter(i => i.vesselId === vessel.id);
  const vesselCAPAs = mockCorrectiveActions.filter(c => c.vesselId === vessel.id);
  const vesselObs = mockObservations.filter(o => vesselInspections.some(i => i.id === o.inspectionId));

  const tabs: { id: TabId; label: string; icon: React.ElementType; count?: number }[] = [
    { id: 'overview', label: 'Overview', icon: Ship },
    { id: 'certificates', label: 'Certificates', icon: Shield, count: vessel.certificates.length },
    { id: 'inspections', label: 'Inspections', icon: ClipboardCheck, count: vesselInspections.length },
    { id: 'capa', label: 'Corrective Actions', icon: AlertTriangle, count: vesselCAPAs.length },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'specifications', label: 'Specifications', icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button onClick={() => navigate('/app/fleet')} className="text-sm text-slate-400 hover:text-sky-600 transition-colors flex items-center gap-1 mb-2">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Fleet
        </button>

        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          {/* Banner */}
          <div className="h-32 bg-gradient-to-r from-[#0A1628] via-[#1E3A5F] to-sky-800 relative">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M20 0v40M0 20h40\' stroke=\'%23ffffff\' stroke-opacity=\'0.05\' stroke-width=\'1\'/%3E%3C/g%3E%3C/svg%3E")' }} />
          </div>

          <div className="px-6 pb-6 -mt-12 relative z-10">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="w-24 h-24 bg-white rounded-2xl shadow-lg border-4 border-white flex items-center justify-center">
                <Ship className="w-10 h-10 text-sky-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{vessel.name}</h1>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                    vessel.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    vessel.status === 'under_inspection' ? 'bg-blue-100 text-blue-700' :
                    vessel.status === 'dry_dock' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {vessel.status === 'dry_dock' ? 'Dry Dock' : vessel.status === 'under_inspection' ? 'Inspecting' : vessel.status}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-1">{vessel.imoNumber} · {vessel.type} · {vessel.flag} · {fleet?.name}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {vessel.location.port}</span>
                  <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Built {vessel.yearBuilt}</span>
                  <span className="flex items-center gap-1"><Weight className="w-3.5 h-3.5" /> {vessel.deadweight.toLocaleString()} DWT</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-xl shadow-lg ${getScoreBgColor(vessel.lastRightShipScore || 0)}`}>
                  {vessel.lastRightShipScore?.toFixed(1) || '—'}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => navigate(`/app/inspections/conduct?vessel=${vessel.id}`)} className="px-4 py-2.5 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 shadow-md shadow-sky-500/20 flex items-center gap-2 transition-all">
                    <ClipboardCheck className="w-4 h-4" /> Inspect
                  </button>
                  <button className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 flex items-center gap-2 transition-all">
                    <Download className="w-4 h-4" /> Export
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto bg-white rounded-2xl border border-slate-100 p-1.5">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-[#0A1628] text-white shadow-md'
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100'
              }`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab vessel={vessel} fleet={fleet} inspections={vesselInspections} observations={vesselObs} />}
      {activeTab === 'certificates' && <CertificatesTab certificates={vessel.certificates} />}
      {activeTab === 'inspections' && <InspectionsTab inspections={vesselInspections} vessel={vessel} navigate={navigate} />}
      {activeTab === 'capa' && <CAPATab capas={vesselCAPAs} />}
      {activeTab === 'analytics' && <AnalyticsTab vessel={vessel} observations={vesselObs} />}
      {activeTab === 'specifications' && <SpecificationsTab vessel={vessel} />}
    </div>
  );
}

// ========================
// OVERVIEW TAB
// ========================
function OverviewTab({ vessel, fleet, inspections, observations }: { vessel: Vessel; fleet: any; inspections: any[]; observations: any[] }) {
  const passCount = observations.filter(o => o.severity === 'observation').length;
  const failCount = observations.filter(o => o.severity === 'critical' || o.severity === 'major').length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Key Metrics */}
      <div className="lg:col-span-2 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Inspections', value: vessel.totalInspections, icon: ClipboardCheck, color: 'bg-sky-50 text-sky-600' },
            { label: 'Open Findings', value: vessel.openFindings, icon: AlertTriangle, color: vessel.openFindings > 5 ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600' },
            { label: 'Overdue CAPAs', value: vessel.overdueActions, icon: Clock, color: vessel.overdueActions > 0 ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600' },
            { label: 'RightShip Score', value: vessel.lastRightShipScore?.toFixed(1) || '—', icon: Award, color: getScoreColor(vessel.lastRightShipScore || 0) ? 'bg-sky-50 text-sky-600' : 'bg-slate-50 text-slate-600' },
          ].map(metric => (
            <div key={metric.label} className={`rounded-xl p-4 ${metric.color.split(' ')[0]}`}>
              <metric.icon className={`w-5 h-5 mb-2 ${metric.color.split(' ')[1]}`} />
              <p className="text-2xl font-bold text-slate-800">{metric.value}</p>
              <p className="text-[10px] text-slate-500 font-medium">{metric.label}</p>
            </div>
          ))}
        </div>

        {/* Location */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-sky-500" /> Current Location
          </h3>
          <div className="h-48 bg-gradient-to-br from-sky-100 to-blue-100 rounded-xl flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\' viewBox=\'0 0 100 100\'%3E%3Cg fill=\'%230284c7\' fill-opacity=\'0.1\'%3E%3Ccircle cx=\'50\' cy=\'50\' r=\'2\'/%3E%3C/g%3E%3C/svg%3E")' }} />
            <div className="text-center relative z-10">
              <MapPin className="w-8 h-8 text-sky-600 mx-auto mb-1" />
              <p className="text-sm font-semibold text-sky-800">{vessel.location.port}</p>
              <p className="text-xs text-sky-600">{vessel.location.lat.toFixed(4)}°N, {vessel.location.lng.toFixed(4)}°E</p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Recent Activity</h3>
          <div className="space-y-3">
            {[
              { text: `Last inspection completed`, date: vessel.lastInspectionDate, icon: CheckCircle2, color: 'text-emerald-500' },
              { text: `Next inspection scheduled`, date: vessel.nextInspectionDate, icon: Calendar, color: 'text-sky-500' },
              { text: `Last RightShip score: ${vessel.lastRightShipScore}/10`, date: vessel.lastInspectionDate, icon: TrendingUp, color: 'text-amber-500' },
            ].map((activity, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <activity.icon className={`w-5 h-5 ${activity.color} flex-shrink-0`} />
                <div className="flex-1">
                  <p className="text-sm text-slate-700">{activity.text}</p>
                  <p className="text-xs text-slate-400">{formatDate(activity.date || '')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Side Panel */}
      <div className="space-y-4">
        {/* Quick Info */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Vessel Details</h3>
          <div className="space-y-3">
            {[
              { label: 'IMO Number', value: vessel.imoNumber },
              { label: 'MMSI', value: vessel.mmsiNumber || '—' },
              { label: 'Flag State', value: vessel.flag },
              { label: 'Classification', value: vessel.classificationSociety },
              { label: 'Manager', value: vessel.manager },
              { label: 'Superintendent', value: vessel.superintendent },
              { label: 'Fleet', value: fleet?.name || '—' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{item.label}</span>
                <span className="font-medium text-slate-700 text-right">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Inspection History */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Inspection History</h3>
          <div className="space-y-2">
            {inspections.slice(0, 5).map(insp => (
              <div key={insp.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  insp.status === 'approved' ? 'bg-emerald-500' :
                  insp.status === 'completed' ? 'bg-blue-500' :
                  insp.status === 'in_progress' ? 'bg-amber-500' : 'bg-slate-300'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-700 truncate">{insp.type.replace('_', ' ')}</p>
                  <p className="text-[10px] text-slate-400">{formatDate(insp.scheduledDate)}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getInspectionStatusColor(insp.status)}`}>
                  {getInspectionStatusLabel(insp.status)}
                </span>
              </div>
            ))}
            {inspections.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">No inspections yet</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Quick Actions</h3>
          <div className="space-y-2">
            {[
              { icon: ClipboardCheck, label: 'Start Inspection', color: 'text-sky-600 bg-sky-50' },
              { icon: Camera, label: 'Upload Evidence', color: 'text-purple-600 bg-purple-50' },
              { icon: FileText, label: 'Generate Report', color: 'text-emerald-600 bg-emerald-50' },
              { icon: Printer, label: 'Print Summary', color: 'text-amber-600 bg-amber-50' },
            ].map(action => (
              <button key={action.label} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left group">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${action.color}`}>
                  <action.icon className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium text-slate-700 group-hover:text-sky-600 transition-colors">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ========================
// CERTIFICATES TAB
// ========================
function CertificatesTab({ certificates }: { certificates: VesselCertificate[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Vessel Certificates</h2>
        <button className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 flex items-center gap-2 shadow-md shadow-sky-500/20">
          Add Certificate
        </button>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Valid', count: certificates.filter(c => c.status === 'valid').length, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
          { label: 'Expiring Soon', count: certificates.filter(c => c.status === 'expiring_soon').length, color: 'bg-amber-50 text-amber-700 border-amber-200' },
          { label: 'Expired', count: certificates.filter(c => c.status === 'expired').length, color: 'bg-red-50 text-red-700 border-red-200' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-3 text-center border ${s.color}`}>
            <p className="text-2xl font-bold">{s.count}</p>
            <p className="text-xs font-medium opacity-70">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Certificate List */}
      <div className="space-y-3">
        {certificates.map(cert => (
          <div key={cert.id} className={`bg-white rounded-2xl border p-5 transition-all hover:shadow-md ${
            cert.status === 'expiring_soon' ? 'border-amber-200' :
            cert.status === 'expired' ? 'border-red-200' : 'border-slate-100'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  cert.status === 'valid' ? 'bg-emerald-50 text-emerald-600' :
                  cert.status === 'expiring_soon' ? 'bg-amber-50 text-amber-600' :
                  'bg-red-50 text-red-600'
                }`}>
                  <Shield className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{cert.name}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Number: {cert.number}</p>
                  <p className="text-xs text-slate-400">Issued by: {cert.issuedBy}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                cert.status === 'valid' ? 'bg-emerald-100 text-emerald-700' :
                cert.status === 'expiring_soon' ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                {cert.status === 'expiring_soon' ? '⚠ Expiring' : cert.status === 'expired' ? '✗ Expired' : '✓ Valid'}
              </span>
            </div>
            <div className="flex items-center gap-6 mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
              <span>Issued: <strong className="text-slate-700">{formatDate(cert.issuedDate)}</strong></span>
              <span>Expires: <strong className={`text-slate-700 ${cert.status === 'expiring_soon' ? 'text-amber-600' : cert.status === 'expired' ? 'text-red-600' : ''}`}>
                {formatDate(cert.expiryDate)}
              </strong></span>
              {cert.status === 'expiring_soon' && (
                <span className="text-amber-600 font-medium">
                  {Math.ceil((new Date(cert.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days remaining
                </span>
              )}
            </div>
          </div>
        ))}
        {certificates.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No certificates on file</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ========================
// INSPECTIONS TAB
// ========================
function InspectionsTab({ inspections, vessel, navigate }: { inspections: any[]; vessel: Vessel; navigate: any }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Inspection History</h2>
        <button onClick={() => navigate(`/app/inspections/conduct?vessel=${vessel.id}`)} className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 flex items-center gap-2 shadow-md shadow-sky-500/20">
          <ClipboardCheck className="w-4 h-4" /> New Inspection
        </button>
      </div>

      {inspections.length > 0 ? (
        <div className="space-y-3">
          {inspections.map(insp => (
            <div key={insp.id} className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-md transition-all cursor-pointer">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${getInspectionStatusColor(insp.status)}`}>
                      {getInspectionStatusLabel(insp.status)}
                    </span>
                    <span className="text-xs text-slate-400 capitalize">{insp.type.replace('_', ' ')}</span>
                  </div>
                  <h3 className="font-semibold text-slate-800 mt-2">{insp.questionnaireName}</h3>
                </div>
                {insp.overallScore && (
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm ${getScoreBgColor(insp.overallScore)}`}>
                    {insp.overallScore}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {insp.inspectorName}</span>
                <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {formatDate(insp.scheduledDate)}</span>
                {insp.completedDate && <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {formatDate(insp.completedDate)}</span>}
              </div>

              {(insp.status === 'completed' || insp.status === 'approved') && (
                <div className="flex gap-4 mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-1 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <span className="text-slate-500">Pass: <strong className="text-slate-700">{insp.passCount}</strong></span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                    <span className="text-slate-500">Fail: <strong className="text-slate-700">{insp.failCount}</strong></span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <span className="text-slate-500">N/A: <strong className="text-slate-700">{insp.naCount}</strong></span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
          <ClipboardCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No inspections recorded for this vessel</p>
          <button onClick={() => navigate(`/app/inspections/conduct?vessel=${vessel.id}`)} className="mt-3 px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600">
            Start First Inspection
          </button>
        </div>
      )}
    </div>
  );
}

// ========================
// CAPA TAB
// ========================
function CAPATab({ capas }: { capas: any[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Corrective Actions</h2>
        <button className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 flex items-center gap-2">
          Add CAPA
        </button>
      </div>

      {capas.length > 0 ? (
        <div className="space-y-3">
          {capas.map(capa => (
            <div key={capa.id} className={`bg-white rounded-2xl border p-5 hover:shadow-md transition-all ${
              capa.isOverdue ? 'border-red-200' : 'border-slate-100'
            }`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                      capa.priority === 'critical' ? 'bg-red-500 text-white' :
                      capa.priority === 'high' ? 'bg-orange-500 text-white' :
                      capa.priority === 'medium' ? 'bg-amber-400 text-amber-900' :
                      'bg-slate-300 text-white'
                    }`}>
                      {capa.priority}
                    </span>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getCAPAStatusColor(capa.status)}`}>
                      {getCAPAStatusLabel(capa.status)}
                    </span>
                    {capa.isOverdue && <span className="text-[10px] font-bold text-red-600">OVERDUE</span>}
                  </div>
                  <h3 className="font-semibold text-slate-800 mt-2">{capa.title}</h3>
                </div>
                {capa.daysRemaining > 0 && (
                  <div className="text-right">
                    <p className={`text-lg font-bold ${capa.daysRemaining < 14 ? 'text-red-600' : 'text-slate-600'}`}>
                      {capa.daysRemaining}
                    </p>
                    <p className="text-[10px] text-slate-400">days left</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 line-clamp-2">{capa.description}</p>
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {capa.owner}</span>
                <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Due: {formatDate(capa.dueDate)}</span>
                {capa.closedDate && <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> Closed: {formatDate(capa.closedDate)}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
          <p className="text-slate-500">No corrective actions — vessel is in good standing!</p>
        </div>
      )}
    </div>
  );
}

// ========================
// ANALYTICS TAB
// ========================
function AnalyticsTab({ vessel, observations }: { vessel: Vessel; observations: any[] }) {
  const severityData = [
    { label: 'Critical', count: observations.filter(o => o.severity === 'critical').length, color: '#EF4444' },
    { label: 'Major', count: observations.filter(o => o.severity === 'major').length, color: '#F97316' },
    { label: 'Minor', count: observations.filter(o => o.severity === 'minor').length, color: '#EAB308' },
    { label: 'Observation', count: observations.filter(o => o.severity === 'observation').length, color: '#94A3B8' },
  ];
  const maxSeverity = Math.max(...severityData.map(s => s.count), 1);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-800">Vessel Analytics</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* RightShip Score */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-4">RightShip Score Trend</h3>
          <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center text-white font-bold text-2xl ${getScoreBgColor(vessel.lastRightShipScore || 0)}`}>
              {vessel.lastRightShipScore?.toFixed(1)}
            </div>
            <div>
              <p className="text-sm text-slate-500">Current Score</p>
              <p className="text-xs text-emerald-600 font-medium">+0.3 from last inspection</p>
            </div>
          </div>
          <div className="mt-4 h-16 flex items-end gap-1">
            {[5.8, 6.2, 6.5, 6.8, 7.0, vessel.lastRightShipScore || 7.2].map((v, i) => (
              <div key={i} className="flex-1 rounded-t-sm bg-sky-400" style={{ height: `${(v / 10) * 100}%`, opacity: 0.4 + (i / 6) * 0.6 }} />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-slate-400 mt-1">
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Now'].map(m => <span key={m}>{m}</span>)}
          </div>
        </div>

        {/* Findings by Severity */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Findings by Severity</h3>
          <div className="space-y-3">
            {severityData.map(s => (
              <div key={s.label}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">{s.label}</span>
                  <span className="font-semibold text-slate-800">{s.count}</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${(s.count / maxSeverity) * 100}%`, backgroundColor: s.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance Metrics */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 md:col-span-2">
          <h3 className="font-semibold text-slate-800 mb-4">Compliance Metrics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total Findings', value: observations.length, icon: AlertTriangle, color: 'text-red-500' },
              { label: 'Inspections', value: vessel.totalInspections, icon: ClipboardCheck, color: 'text-sky-500' },
              { label: 'Open Items', value: vessel.openFindings, icon: Clock, color: 'text-amber-500' },
              { label: 'Overdue', value: vessel.overdueActions, icon: AlertTriangle, color: 'text-red-600' },
            ].map(metric => (
              <div key={metric.label} className="text-center p-4 bg-slate-50 rounded-xl">
                <metric.icon className={`w-6 h-6 mx-auto mb-2 ${metric.color}`} />
                <p className="text-2xl font-bold text-slate-800">{metric.value}</p>
                <p className="text-[10px] text-slate-500 font-medium">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ========================
// SPECIFICATIONS TAB
// ========================
function SpecificationsTab({ vessel }: { vessel: Vessel }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-slate-800">Vessel Specifications</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Dimensions */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Ruler className="w-4 h-4 text-sky-500" /> Dimensions
          </h3>
          <div className="space-y-3">
            {[
              { label: 'Length Overall', value: `${vessel.specifications.lengthOverall} m` },
              { label: 'Beam', value: `${vessel.specifications.beam} m` },
              { label: 'Depth', value: `${vessel.specifications.depth} m` },
              { label: 'Draft', value: `${vessel.specifications.draft} m` },
            ].map(spec => (
              <div key={spec.label} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-500">{spec.label}</span>
                <span className="text-sm font-semibold text-slate-800">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Capacity & Performance */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Gauge className="w-4 h-4 text-sky-500" /> Capacity & Performance
          </h3>
          <div className="space-y-3">
            {[
              { label: 'Deadweight', value: `${vessel.deadweight.toLocaleString()} DWT` },
              { label: 'Gross Tonnage', value: `${vessel.grossTonnage.toLocaleString()} GT` },
              { label: 'Service Speed', value: `${vessel.specifications.speed} knots` },
              { label: 'Year Built', value: vessel.yearBuilt.toString() },
            ].map(spec => (
              <div key={spec.label} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-500">{spec.label}</span>
                <span className="text-sm font-semibold text-slate-800">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Machinery */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 md:col-span-2">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-sky-500" /> Machinery
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'Main Engine', value: vessel.specifications.engineType },
              { label: 'Classification Society', value: vessel.classificationSociety },
              { label: 'Flag State', value: vessel.flag },
              { label: 'IMO Number', value: vessel.imoNumber },
            ].map(spec => (
              <div key={spec.label} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-500">{spec.label}</span>
                <span className="text-sm font-semibold text-slate-800 text-right">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
